import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, TrendingDown } from 'lucide-react';
import { getUserAnalytics } from '../services/analyticsService.js';

const SkillRadarChart = ({ userId, compact = false }) => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSkills = async () => {
      setLoading(true);
      const analytics = await getUserAnalytics(userId);
      if (analytics && analytics.skillBreakdown.length > 0) {
        // Take top 6 skills for visualization
        const topSkills = analytics.skillBreakdown
          .sort((a, b) => b.averageScore - a.averageScore)
          .slice(0, 6);
        setSkills(topSkills);
      }
      setLoading(false);
    };

    if (userId) {
      fetchSkills();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-3 text-gray-600">Loading skill analysis...</span>
        </div>
      </div>
    );
  }

  if (skills.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No skill data available yet</p>
          <p className="text-sm text-gray-500 mt-2">Complete tests to see your skill breakdown!</p>
        </div>
      </div>
    );
  }

  // Calculate radar chart points
  const centerX = 200;
  const centerY = 200;
  const radius = 140;
  const maxScore = 100;

  // Generate polygon points for skill scores
  const getRadarPoints = (scores) => {
    const angleStep = (2 * Math.PI) / scores.length;
    return scores.map((score, index) => {
      const angle = angleStep * index - Math.PI / 2; // Start from top
      const distance = (score / maxScore) * radius;
      const x = centerX + distance * Math.cos(angle);
      const y = centerY + distance * Math.sin(angle);
      return `${x},${y}`;
    }).join(' ');
  };

  // Generate grid circles
  const gridLevels = [25, 50, 75, 100];

  // Generate axis lines and labels
  const angleStep = (2 * Math.PI) / skills.length;
  const axes = skills.map((skill, index) => {
    const angle = angleStep * index - Math.PI / 2;
    const endX = centerX + radius * Math.cos(angle);
    const endY = centerY + radius * Math.sin(angle);

    // Label position (slightly beyond the radius)
    const labelDistance = radius + 40;
    const labelX = centerX + labelDistance * Math.cos(angle);
    const labelY = centerY + labelDistance * Math.sin(angle);

    return { endX, endY, labelX, labelY, skill, angle };
  });

  const radarPoints = getRadarPoints(skills.map(s => s.averageScore));

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <span className="text-xs text-gray-500">→</span>;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'text-green-600 bg-green-50';
      case 'down':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${compact ? 'p-4' : 'p-6'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Target className="w-6 h-6 text-red-600" />
          <h2 className={`${compact ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>
            Skills Analysis
          </h2>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className="flex items-center justify-center">
          <svg width="400" height="400" viewBox="0 0 400 400" className="max-w-full">
            {/* Grid circles */}
            {gridLevels.map((level) => (
              <circle
                key={level}
                cx={centerX}
                cy={centerY}
                r={(level / maxScore) * radius}
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="1"
              />
            ))}

            {/* Axes */}
            {axes.map((axis, index) => (
              <g key={index}>
                <line
                  x1={centerX}
                  y1={centerY}
                  x2={axis.endX}
                  y2={axis.endY}
                  stroke="#D1D5DB"
                  strokeWidth="1"
                />
              </g>
            ))}

            {/* Score polygon */}
            <polygon
              points={radarPoints}
              fill="rgba(239, 68, 68, 0.2)"
              stroke="#EF4444"
              strokeWidth="2"
            />

            {/* Score points */}
            {skills.map((skill, index) => {
              const angle = angleStep * index - Math.PI / 2;
              const distance = (skill.averageScore / maxScore) * radius;
              const x = centerX + distance * Math.cos(angle);
              const y = centerY + distance * Math.sin(angle);

              return (
                <g key={index}>
                  <circle
                    cx={x}
                    cy={y}
                    r="5"
                    fill="#DC2626"
                    className="hover:r-7 transition-all cursor-pointer"
                  />
                  <title>{skill.skillName}: {skill.averageScore}%</title>
                </g>
              );
            })}

            {/* Labels */}
            {axes.map((axis, index) => {
              // Calculate text anchor based on position
              let textAnchor = 'middle';
              if (axis.labelX < centerX - 10) textAnchor = 'end';
              else if (axis.labelX > centerX + 10) textAnchor = 'start';

              return (
                <text
                  key={index}
                  x={axis.labelX}
                  y={axis.labelY}
                  textAnchor={textAnchor}
                  className="text-xs font-semibold fill-gray-700"
                >
                  <tspan x={axis.labelX} dy="0">{axis.skill.skillName}</tspan>
                  <tspan x={axis.labelX} dy="14" className="font-bold fill-red-600">
                    {axis.skill.averageScore}%
                  </tspan>
                </text>
              );
            })}

            {/* Center dot */}
            <circle cx={centerX} cy={centerY} r="3" fill="#6B7280" />
          </svg>
        </div>

        {/* Skills List */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Skill Breakdown</h3>

          {skills.map((skill, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-red-300 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2 flex-1">
                  <span className="font-semibold text-gray-900">{skill.skillName}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTrendColor(skill.trend)}`}>
                    {getTrendIcon(skill.trend)}
                  </span>
                </div>
                <span className="text-2xl font-bold text-red-600">{skill.averageScore}%</span>
              </div>

              {/* Progress bar */}
              <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-500"
                  style={{ width: `${skill.averageScore}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer" />
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>{skill.testCount} test{skill.testCount !== 1 ? 's' : ''}</span>
                <span>Last tested: {new Date(skill.lastTested).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Legend */}
      {!compact && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-gray-600">Your Score</span>
            </div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-gray-600">Improving</span>
            </div>
            <div className="flex items-center space-x-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="text-gray-600">Declining</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillRadarChart;

